import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import icPersonAdd from '@iconify/icons-ic/twotone-person-add';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SupplierContactCreateUpdateDeleteComponent } from '../../modals/supplier-contact-create-update-delete/supplier-contact-create-update-delete.component';
import { SupplierContactDeleteConfirmationModalComponent } from '../../modals/supplier-contact-delete-confirmation-modal/supplier-contact-delete-confirmation-modal.component';
import { SupplierService } from '../../supplier.service';
import { DependenciesService } from '../../../../../../core/services/dependencies.service';
import { SupplierContactWrapperService } from '../../supplier-contact-wrapper.service';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import icSearch from '@iconify/icons-ic/twotone-search';
import icClose from '@iconify/icons-ic/twotone-close';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { SupplierContactModel } from '../../../../../../core/models/supplier-contact.model';
import { EstablishmentService } from '../../../establishment/establishment.service';
import { AuthService } from '../../../../../../core/services/auth.service';

@Component({
  selector: 'vex-supplier-details-contact',
  templateUrl: './supplier-details-contact.component.html',
  styleUrls: ['./supplier-details-contact.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
})
export class SupplierDetailsContactComponent implements OnInit, AfterViewInit, OnDestroy {
  ENUM_PERMISSION = ENUM_PERMISSIONS;
  private subscription = new Subscription();
  private initDataDestroy$ = new Subject<void>();
  icClose = icClose;
  icSearch = icSearch;
  icPersonAdd = icPersonAdd;
  contacts = [];
  loading = false;
  supplierId: string;
  isInternSupplier = false;
  @ViewChild('search') search: ElementRef;

  constructor(
    private dialog: MatDialog,
    public supplierService: SupplierService,
  ) {}

  ngOnInit(): void {
    this.initData();
  }

  ngAfterViewInit() {
    this.subscription.add(
      fromEvent(this.search.nativeElement, 'keyup')
        .pipe(
          map(value => {
            return value;
          }),
          debounceTime(150),
          distinctUntilChanged(),
          tap(value => {
            this.supplierService.searchContacts(this.search.nativeElement.value);
            // this.onSearchChange.emit(this.search.nativeElement.value);
          }),
        )
        .subscribe(),
    );
  }

  initData() {
    this.supplierService.searchContacts('');
    // Annuler le subscribe précédent pour éviter les subscribes multiples
    this.initDataDestroy$.next();
    this.supplierService.entity$.pipe(takeUntil(this.initDataDestroy$)).subscribe(x => {
      this.isInternSupplier = !!x.supportEstablishment;
      x.contacts.map(contact => {
        contact.fullName = `${contact.firstName} ${contact.lastName}`;
        return contact;
      });
      this.supplierService.setContacts(x.contacts ?? []);
    });
  }

  openContact(contact: any) {
    const sub = this.dialog
      .open(SupplierContactCreateUpdateDeleteComponent, {
        data: {
          defaults: contact,
          mode: CrudModeEnum.Read,
        },
      })
      .afterClosed()
      .subscribe(res => {
        console.log('DEBUG - Modal closed with response:', res);
        if (res) {
          console.log('DEBUG - Calling initData() to refresh');
          this.initData();
          this.supplierService.triggerMissingContactsRefresh();
        }
      });
    this.subscription.add(sub);
  }

  async createContact() {
    const { _id } = await this.supplierService.entity;
    const sub = this.dialog
      .open(SupplierContactCreateUpdateDeleteComponent, {
        data: {
          id: _id,
          mode: CrudModeEnum.Create,
        },
        width: '800px',
      })
      .afterClosed()
      .subscribe(res => {
        console.log('DEBUG - Modal closed with response:', res);
        if (res) {
          console.log('DEBUG - Calling initData() to refresh');
          this.initData();
          this.supplierService.triggerMissingContactsRefresh();
        }
      });
    this.subscription.add(sub);
  }

  importItem() {
    const sub = this.dialog
      .open(SupplierContactCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Import,
        },
      })
      .afterClosed()
      .subscribe((supplier: SupplierContactModel) => {
        if (supplier) {
          this.initData();
          this.supplierService.triggerMissingContactsRefresh();
        }
      });
    this.subscription.add(sub);
  }

  async editContact(contact) {
    const { _id } = await this.supplierService.entity;
    if (this.isInternSupplier) {
      return;
    }
    const sub = this.dialog
      .open(SupplierContactCreateUpdateDeleteComponent, {
        data: {
          id: _id,
          defaults: contact,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe({
        next: res => {
          console.log('Modal closed with:', res);
          if (res) {
            this.initData();
            this.supplierService.triggerMissingContactsRefresh();
          }
        },
        error: err => console.error('Error closing modal:', err),
      });
    this.subscription.add(sub);
  }

  async deleteContact(contact: SupplierContactModel) {
    const { _id } = await this.supplierService.entity;
    if (this.isInternSupplier) {
      return;
    }

    const dialogSub = this.dialog
      .open(SupplierContactDeleteConfirmationModalComponent, {
        data: { supplierId: _id, contactId: contact._id },
        width: '600px',
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed === 'unitary') {
          this.openUnitaryDeletionEdit(_id, contact);
        } else if (confirmed) {
          const sub = this.supplierService.deleteContact(_id, contact._id).subscribe(
            () => {
              this.initData();
              this.supplierService.triggerMissingContactsRefresh();
            },
            error => console.error('Erreur lors de la suppression du contact:', error),
          );
          this.subscription.add(sub);
        }
      });
    this.subscription.add(dialogSub);
  }

  private openUnitaryDeletionEdit(supplierId: string, contact: SupplierContactModel) {
    const sub = this.dialog
      .open(SupplierContactCreateUpdateDeleteComponent, {
        data: {
          id: supplierId,
          defaults: contact,
          mode: CrudModeEnum.Update,
          unitaryDeletionMode: true,
        },
      })
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.initData();
        }
      });
    this.subscription.add(sub);
  }

  clearTextFilter() {
    this.search.nativeElement.value = '';
    this.supplierService.searchContacts('');
  }

  ngOnDestroy() {
    this.initDataDestroy$.next();
    this.initDataDestroy$.complete();
    this.subscription.unsubscribe();
  }
}
